import React, { useEffect, useState } from 'react'
import { useAuth } from '../../../context/useAuth'
import { useNls } from '../../../nls/nlsProvider'
import type { OpsCustomer } from '../../../types/ops'
import type { Workspace, WorkspaceRole } from '../../../types/workspace'
import { Button, Loader } from '../../Common'
import { useIsPhone } from '../../Common/useIsMobile'
import { subscribeMyCustomerRecord } from '../../../services/opsBookingsService'
import { MemberLinkBookingsCard } from './MemberLinkBookingsCard'
import { MemberBookingsListTab } from './MemberBookingsListTab'
import { MemberBookTab } from './MemberBookTab'
import { MemberPlansAndDuesTab } from './MemberPlansAndDuesTab'
import { MemberBookingQrModal } from './MemberBookingQrModal'
import { MemberScanPlaceQrModal } from './MemberScanPlaceQrModal'
import './memberBookings.css'

export interface MemberBookingsViewProps {
  workspace: Workspace
  role: WorkspaceRole
  isMobile?: boolean
}

export type MemberBookingsSubTab = 'UPCOMING' | 'BOOK' | 'PLANS'

export function MemberBookingsView({
  workspace,
}: MemberBookingsViewProps): React.ReactElement {
  const { t } = useNls()
  const { currentUser, isAuthLoading } = useAuth()
  const isPhone = useIsPhone()

  const [customer, setCustomer] = useState<OpsCustomer | null>(null)
  const [customerLoaded, setCustomerLoaded] = useState<boolean>(false)
  const [activeSubTab, setActiveSubTab] = useState<MemberBookingsSubTab>('UPCOMING')
  const [qrModalOpen, setQrModalOpen] = useState(false)
  const [scanModalOpen, setScanModalOpen] = useState(false)

  const loading = isAuthLoading || (!customerLoaded && Boolean(workspace.id && currentUser?.uid))

  useEffect(() => {
    if (!workspace.id || !currentUser?.uid) {
      return
    }

    const unsub = subscribeMyCustomerRecord(
      workspace.id,
      currentUser.uid,
      (cust) => {
        setCustomer(cust)
        setCustomerLoaded(true)
      },
      () => {
        setCustomer(null)
        setCustomerLoaded(true)
      },
    )

    return () => {
      if (typeof unsub === 'function') unsub()
    }
  }, [workspace.id, currentUser?.uid])

  if (loading || isAuthLoading) {
    return (
      <div className="memberBookingsView">
        <Loader />
      </div>
    )
  }

  if (!customer) {
    return (
      <div className={`memberBookingsView ${isPhone ? 'memberBookingsView--phone' : ''}`}>
        <header className="memberBookingsView__header">
          <h1 className="memberBookingsView__title">{t('NLSMemberBookingsTitle')}</h1>
          <p className="memberBookingsView__subtitle">{t('NLSMemberBookingsSubtitle')}</p>
        </header>
        <MemberLinkBookingsCard
          workspace={workspace}
          onLinked={() => {
            // Re-subscribe or wait for snapshot update
          }}
        />
      </div>
    )
  }

  return (
    <div className={`memberBookingsView ${isPhone ? 'memberBookingsView--phone' : ''}`}>
      <header className="memberBookingsView__header">
        <div className="memberBookingsView__titleRow">
          <div>
            <h1 className="memberBookingsView__title">{t('NLSMemberBookingsTitle')}</h1>
            <p className="memberBookingsView__subtitle">{t('NLSMemberBookingsSubtitle')}</p>
          </div>

          <div className="memberBookingsView__topActions">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setQrModalOpen(true)}
            >
              {t('NLSMemberBookingsMyQrAction')}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setScanModalOpen(true)}
            >
              {t('NLSMemberBookingsScanQrAction')}
            </Button>
          </div>
        </div>

        <nav className="memberBookingsView__tabs" aria-label={t('NLSMemberBookingsTitle')}>
          <Button
            variant="tertiary"
            className={`memberBookingsView__tabBtn ${activeSubTab === 'UPCOMING' ? 'memberBookingsView__tabBtn--active' : ''}`}
            onClick={() => setActiveSubTab('UPCOMING')}
          >
            {t('NLSMemberBookingsTabUpcoming')}
          </Button>
          <Button
            variant="tertiary"
            className={`memberBookingsView__tabBtn ${activeSubTab === 'BOOK' ? 'memberBookingsView__tabBtn--active' : ''}`}
            onClick={() => setActiveSubTab('BOOK')}
          >
            {t('NLSMemberBookingsTabBook')}
          </Button>
          <Button
            variant="tertiary"
            className={`memberBookingsView__tabBtn ${activeSubTab === 'PLANS' ? 'memberBookingsView__tabBtn--active' : ''}`}
            onClick={() => setActiveSubTab('PLANS')}
          >
            {t('NLSMemberBookingsTabPlans')}
          </Button>
        </nav>
      </header>

      <main>
        {activeSubTab === 'UPCOMING' && (
          <MemberBookingsListTab workspace={workspace} customer={customer} />
        )}

        {activeSubTab === 'BOOK' && (
          <MemberBookTab
            workspace={workspace}
            customer={customer}
            isPhone={isPhone}
          />
        )}

        {activeSubTab === 'PLANS' && (
          <MemberPlansAndDuesTab workspace={workspace} customer={customer} />
        )}
      </main>

      {/* QR Modals */}
      <MemberBookingQrModal
        open={qrModalOpen}
        onClose={() => setQrModalOpen(false)}
        workspace={workspace}
      />

      <MemberScanPlaceQrModal
        open={scanModalOpen}
        onClose={() => setScanModalOpen(false)}
      />
    </div>
  )
}
